export const TaskStatusE = {
	/**
	 * Frontend-only optimistic status: the task has not been submitted to the
	 * API yet (e.g. because we are still waiting for the upstream build to
	 * complete). Never returned by the backend.
	 */
	Pending: "Pending",
	/**
	 * Frontend-only terminal status: the task was never submitted to the API
	 * because the upstream build did not complete successfully or the build
	 * request never reached the backend. Distinct from Cancelled, which the
	 * backend uses for tasks it explicitly stopped. Never returned by the
	 * backend.
	 */
	NotStarted: "Not started",
	Initialising: "Initialising",
	Accepted: "Accepted",
	InProgress: "In Progress",
	Completed: "Completed",
	Cancelled: "Cancelled",
	Stopped: "Stopped",
	Rescheduled: "Rescheduled",
	NotFoundError: "Task Not Found",
	Forbidden: "Task Rejected",
	Error: "Error",
	UpgradeAccount: "Upgrade Your Account",
	TierLimited: "Tier Limit Reached",
} as const;
export type TaskStatus = (typeof TaskStatusE)[keyof typeof TaskStatusE];

export type TaskStatusTransition = {
	Status: TaskStatus;
	Timestamp: number;
	Message?: string;
};

export function isTaskActive(taskStatus: TaskStatus): boolean {
	const activeStatuses = [
		TaskStatusE.Pending,
		TaskStatusE.Accepted,
		TaskStatusE.Initialising,
		TaskStatusE.Rescheduled,
		TaskStatusE.InProgress,
	];
	return activeStatuses.some((s) => s === taskStatus);
}

export function isTaskTerminated(taskStatus: TaskStatus): boolean {
	const activeStatuses = [
		TaskStatusE.Completed,
		TaskStatusE.Cancelled,
		TaskStatusE.NotStarted,
		TaskStatusE.Stopped,
		TaskStatusE.Error,
		TaskStatusE.NotFoundError,
		TaskStatusE.Forbidden,
		TaskStatusE.TierLimited,
	];
	return activeStatuses.some((s) => s === taskStatus);
}

export function isTaskSuccess(taskStatus: TaskStatus): boolean {
	const activeStatuses = [TaskStatusE.Completed];
	return activeStatuses.some((s) => s === taskStatus);
}
export function isTaskFail(taskStatus: TaskStatus): boolean {
	const activeStatuses = [TaskStatusE.Cancelled, TaskStatusE.Stopped];
	return activeStatuses.some((s) => s === taskStatus);
}

/*
 * Response types
 */

export type TaskStatistics = {
	DynamicInstructions: number;
	ProcessorTime: number;
	ExecutionTimeInMilliseconds?: number;
};

export type Task = {
	TaskID: string;
	Owner: string;
	BuildID: string;
	Arguments: string;
	Status: TaskStatus;
	StatusTransitions: TaskStatusTransition[];
	Stats?: TaskStatistics;
	StartedAt: number /* The time the Task.Status becomes "In Progress" */;
	UpdatedAt: number;
	CreatedAt: number /* The time the Task.Status becomes "Accepted" */;
	FinishedAt: number /* The time the Task.Status becomes "Completed", "Cancelled", or "Stopped" */;
};

export type TaskWithStats = {
	TaskID: string;
	Owner: string;
	BuildID: string;
	Status: TaskStatus;
	StatusTransitions: TaskStatusTransition[];
	Stats?: TaskStatistics;
	StartedAt: number /* The time the Task.Status becomes "In Progress" */;
	UpdatedAt: number;
	CreatedAt: number /* The time the Task.Status becomes "Accepted" */;
	FinishedAt: number /* The time the Task.Status becomes "Completed", "Cancelled", or "Stopped" */;
};

export const TaskOutputStreamsE = {
	stdout: "stdout",
	stderr: "stderr",
} as const;
export type TaskOutputStreams = (typeof TaskOutputStreamsE)[keyof typeof TaskOutputStreamsE];

// FIXME: how can i make this dynamic from TaskOutputStreams
export type TaskOutput = {
	[key: string]: string;
};
